import { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { Entity } from '@backstage/catalog-model';
import { Content, Header, Page, Progress } from '@backstage/core-components';
import {
  Grid,
  Card,
  CardActionArea,
  CardContent,
  CardActions,
  Button,
  IconButton,
  Tooltip,
  Typography,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  makeStyles,
} from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import DescriptionIcon from '@material-ui/icons/Description';
import MenuBookIcon from '@material-ui/icons/MenuBook';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';

const SAFE_URL_PROTOCOLS = new Set(['http:', 'https:', 'mailto:']);
const DESCRIPTION_TRUNCATE_THRESHOLD = 140;

function isSafeUrl(url: string): boolean {
  try {
    return SAFE_URL_PROTOCOLS.has(new URL(url).protocol);
  } catch {
    return false;
  }
}

const useStyles = makeStyles(theme => ({
  card: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  cardActionArea: {
    flexGrow: 1,
  },
  cardContent: {
    height: '100%',
    padding: theme.spacing(3),
  },
  description: {
    display: '-webkit-box',
    WebkitLineClamp: 3,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  tags: {
    display: 'flex',
    flexWrap: 'wrap',
    padding: theme.spacing(0, 3, 2),
    '& > *': {
      margin: theme.spacing(0.25),
    },
  },
  seeMoreButton: {
    padding: theme.spacing(0, 3),
    marginBottom: theme.spacing(1),
    textTransform: 'none',
    color: theme.palette.text.secondary,
  },
  cardActions: {
    padding: theme.spacing(1, 2, 2),
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    '& > *': {
      margin: theme.spacing(0.5),
    },
  },
  cardActionsLinks: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  backButton: {
    marginBottom: theme.spacing(3),
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(6),
  },
}));

type Classes = ReturnType<typeof useStyles>;

function ComponentTile({
  entity,
  classes,
  navigate,
}: {
  entity: Entity;
  classes: Classes;
  navigate: ReturnType<typeof useNavigate>;
}) {
  const [dialogOpen, setDialogOpen] = useState(false);

  const links = (entity.metadata.links ?? []).filter(link =>
    isSafeUrl(link.url),
  );
  const tags = entity.metadata.tags ?? [];
  const title = (entity.metadata.title as string) || entity.metadata.name;
  const description = entity.metadata.description as string | undefined;
  const isLongDescription =
    !!description && description.length > DESCRIPTION_TRUNCATE_THRESHOLD;

  const hasTechDocs =
    !!entity.metadata.annotations?.['backstage.io/techdocs-ref'];
  const namespace = entity.metadata.namespace || 'default';
  const kind = entity.kind.toLowerCase();

  const sourceLocation =
    entity.metadata.annotations?.['backstage.io/source-location'];
  const repoUrl = sourceLocation?.replace(/^url:/, '');
  const branch =
    entity.metadata.annotations?.['kaizen/default-branch'] ?? 'main';
  const readmeUrl =
    !hasTechDocs && repoUrl && isSafeUrl(repoUrl)
      ? `${repoUrl}/-/blob/${branch}/README.md`
      : undefined;

  const primaryLink = links[0];
  const secondaryLinks = links.slice(1);
  const catalogUrl = `/catalog/${namespace}/${kind}/${entity.metadata.name}`;

  const handleCloseDialog = () => setDialogOpen(false);

  const cardBody = (
    <CardContent className={classes.cardContent}>
      <Typography variant="h6" gutterBottom>
        {title}
      </Typography>
      {description && (
        <Typography
          variant="body2"
          color="textSecondary"
          paragraph
          className={isLongDescription ? classes.description : undefined}
        >
          {description}
        </Typography>
      )}
    </CardContent>
  );

  return (
    <>
      <Grid item xs={12} sm={6} md={4}>
        <Card className={classes.card} elevation={2}>
          {primaryLink ? (
            <CardActionArea
              className={classes.cardActionArea}
              href={primaryLink.url}
              target="_blank"
              rel="noopener noreferrer"
            >
              {cardBody}
            </CardActionArea>
          ) : (
            <CardActionArea
              className={classes.cardActionArea}
              onClick={() => navigate(catalogUrl)}
            >
              {cardBody}
            </CardActionArea>
          )}
          {isLongDescription && (
            <Button
              className={classes.seeMoreButton}
              size="small"
              onClick={() => setDialogOpen(true)}
            >
              Lire la description complète
            </Button>
          )}
          {tags.length > 0 && (
            <div className={classes.tags}>
              {tags.map(tag => (
                <Chip key={tag} label={tag} size="small" />
              ))}
            </div>
          )}
          {(secondaryLinks.length > 0 ||
            hasTechDocs ||
            readmeUrl ||
            primaryLink) && (
            <CardActions className={classes.cardActions}>
              <div className={classes.cardActionsLinks}>
                {hasTechDocs && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<MenuBookIcon />}
                    onClick={() =>
                      navigate(
                        `/docs/${namespace}/${kind}/${entity.metadata.name}`,
                      )
                    }
                  >
                    Docs
                  </Button>
                )}
                {readmeUrl && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<DescriptionIcon />}
                    href={readmeUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    README
                  </Button>
                )}
                {secondaryLinks.map(link => (
                  <Button
                    key={link.url}
                    variant="outlined"
                    size="small"
                    endIcon={<OpenInNewIcon />}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {link.title}
                  </Button>
                ))}
              </div>
              {primaryLink && (
                <Tooltip title="Voir la fiche catalogue Backstage">
                  <IconButton
                    size="small"
                    onClick={() => navigate(catalogUrl)}
                    aria-label="Voir la fiche catalogue"
                  >
                    <InfoOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </CardActions>
          )}
        </Card>
      </Grid>
      {isLongDescription && (
        <Dialog
          open={dialogOpen}
          onClose={handleCloseDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>{title}</DialogTitle>
          <DialogContent>
            <Typography variant="body2">{description}</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Fermer</Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
}

export const SystemPage = () => {
  const { systemName } = useParams<{ systemName: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const navState = location.state as { title?: string } | null;
  const catalogApi = useApi(catalogApiRef);
  const classes = useStyles();
  const [entities, setEntities] = useState<Entity[]>([]);
  const [systemTitle, setSystemTitle] = useState<string>(navState?.title ?? '');
  const [systemSubtitle, setSystemSubtitle] = useState<string>('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!systemName) return;

    Promise.all([
      catalogApi.getEntities({
        filter: { kind: 'Component', 'spec.system': systemName },
      }),
      catalogApi
        .getEntityByRef(`system:default/${systemName}`)
        .catch(() => undefined),
    ]).then(([components, system]) => {
      setEntities(components.items);
      if (system) {
        setSystemTitle(
          (system.metadata.title as string) || system.metadata.name,
        );
        setSystemSubtitle(system.metadata.description ?? '');
      }
      setLoading(false);
    });
  }, [systemName, catalogApi]);

  if (loading) {
    return (
      <Page themeId="home">
        <Header title="Chargement..." />
        <Content>
          <Progress />
        </Content>
      </Page>
    );
  }

  return (
    <Page themeId="home">
      <Header
        title={systemTitle || systemName || ''}
        subtitle={systemSubtitle}
      />
      <Content>
        <div className={classes.backButton}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)}>
            Retour
          </Button>
        </div>

        {entities.length === 0 ? (
          <div className={classes.emptyState}>
            <Typography variant="h6" color="textSecondary">
              Aucun composant trouvé dans ce système.
            </Typography>
          </div>
        ) : (
          <Grid container spacing={3}>
            {entities.map(entity => (
              <ComponentTile
                key={entity.metadata.name}
                entity={entity}
                classes={classes}
                navigate={navigate}
              />
            ))}
          </Grid>
        )}
      </Content>
    </Page>
  );
};
